import { forwardRef } from "react";

import { SuggestionListContainer, SuggestionRow, useSuggestionListNav } from "./suggestion-list";
import type { ChipListHandle } from "./chip-suggestion";
import { slashShortcutGlyphs, type SlashItem } from "./slash-items";

export interface SlashCommandListProps {
  items: SlashItem[];
  command: (item: SlashItem) => void;
}

export const SlashCommandList = forwardRef<ChipListHandle, SlashCommandListProps>(
  ({ items, command }, ref) => {
    const selectItem = (index: number) => {
      const item = items[index];
      if (item) command(item);
    };

    const { selectedIndex, setSelectedIndex, itemRef } = useSuggestionListNav<HTMLButtonElement>(
      ref,
      items.length,
      selectItem,
      items,
    );

    return (
      <SuggestionListContainer
        className="slash-command-popup w-72"
        empty={items.length === 0}
        emptyMessage="No matching blocks"
      >
        {items.map((item, index) => {
          const Icon = item.icon;
          return (
            <SuggestionRow
              key={item.id}
              index={index}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              itemRef={itemRef}
              onSelect={selectItem}
            >
              <span className="flex size-6 shrink-0 items-center justify-center text-muted-foreground">
                <Icon className="size-4" />
              </span>
              <span className="min-w-0 flex-1 truncate">{item.title}</span>
              {item.shortcut ? (
                <span className="ml-auto flex shrink-0 items-center gap-0.5 text-[10px] tabular-nums text-muted-foreground">
                  {slashShortcutGlyphs(item.shortcut).map((glyph) => (
                    <kbd
                      key={glyph}
                      className="rounded px-1 font-sans font-medium text-muted-foreground/80"
                    >
                      {glyph}
                    </kbd>
                  ))}
                </span>
              ) : null}
            </SuggestionRow>
          );
        })}
      </SuggestionListContainer>
    );
  },
);

SlashCommandList.displayName = "SlashCommandList";
